import React, { useState, useEffect } from 'react';
import { lessonsAPI } from '../../services/api';
import './TeacherPages.css';

const Lessons = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    titleJapanese: '',
    description: '',
    level: 'N5',
    category: 'General'
  });
  
  const [lessonForm, setLessonForm] = useState({
    title: '',
    titleJapanese: '',
    description: '',
    content: '',
    lessonOrder: 1,
    durationMinutes: 60
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await lessonsAPI.getCourses();
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Courses fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      await lessonsAPI.createCourse(courseForm);
      alert('Course created successfully!\nコースが作成されました！');
      setShowCourseForm(false);
      setCourseForm({ title: '', titleJapanese: '', description: '', level: 'N5', category: 'General' });
      fetchCourses();
    } catch (error) {
      alert('Failed to create course: ' + error.message);
    }
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    try {
      await lessonsAPI.createLesson({
        ...lessonForm,
        courseId: selectedCourse.id
      });
      alert('Lesson created successfully!\nレッスンが作成されました！');
      setShowLessonForm(false);
      setLessonForm({ title: '', titleJapanese: '', description: '', content: '', lessonOrder: 1, durationMinutes: 60 });
      fetchCourses();
    } catch (error) {
      alert('Failed to create lesson: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex-center" style={{ height: '50vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="lessons-management fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1>📝 Lesson Management</h1>
          <p className="page-subtitle">レッスン管理 - Create and manage courses</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCourseForm(true)}>
          + Create Course
        </button>
      </div>

      {/* コース一覧 */}
      <div style={{ display: 'grid', gap: '16px' }}>
        {courses.map(course => (
          <div 
            key={course.id}
            style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              background: 'var(--color-secondary)',
              color: 'white'
            }}>
              <div>
                <span className={`level-badge level-${course.level?.toLowerCase()}`} style={{ marginRight: '12px' }}>
                  {course.level}
                </span>
                <span style={{ fontSize: '18px', fontWeight: '600' }}>{course.title}</span>
                <span style={{ marginLeft: '12px', opacity: 0.8 }}>{course.title_japanese}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-sm"
                  style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                  onClick={() => { setSelectedCourse(course); setShowLessonForm(true); }}
                >
                  + Add Lesson
                </button>
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ color: 'var(--color-text-light)', marginBottom: '12px' }}>
                {course.description}
              </p>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--color-text-light)' }}>
                <span>📚 {course.total_lessons || 0} lessons</span>
                <span>🏷️ {course.category}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {courses.length === 0 && (
        <div className="empty-state" style={{ marginTop: '24px' }}>
          <p>No courses yet</p>
          <p className="text-muted">まだコースがありません</p>
          <button className="btn btn-primary" onClick={() => setShowCourseForm(true)}>
            Create Your First Course
          </button>
        </div>
      )}

      {/* コース作成モーダル */}
      {showCourseForm && (
        <div className="booking-modal" onClick={() => setShowCourseForm(false)}>
          <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📚 Create New Course / 新しいコースを作成</h2>
            
            <form onSubmit={handleCreateCourse}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Title (English)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    placeholder="e.g., N5 Basic Japanese"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Title (Japanese / 日本語)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={courseForm.titleJapanese}
                    onChange={(e) => setCourseForm({ ...courseForm, titleJapanese: e.target.value })}
                    placeholder="例：N5 基礎日本語"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  placeholder="Describe the course content..."
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Level</label>
                  <select
                    className="form-select"
                    value={courseForm.level}
                    onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                  >
                    <option value="N5">N5 - Beginner</option>
                    <option value="N4">N4 - Elementary</option>
                    <option value="N3">N3 - Intermediate</option>
                    <option value="N2">N2 - Upper Intermediate</option>
                    <option value="N1">N1 - Advanced</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={courseForm.category}
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                  >
                    <option value="General">General</option>
                    <option value="Grammar">Grammar</option>
                    <option value="Conversation">Conversation</option>
                    <option value="Business">Business</option>
                    <option value="JLPT">JLPT Preparation</option>
                  </select>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowCourseForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* レッスン作成モーダル */}
      {showLessonForm && selectedCourse && (
        <div className="booking-modal" onClick={() => setShowLessonForm(false)}>
          <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📝 Add Lesson to {selectedCourse.title}</h2>
            
            <form onSubmit={handleCreateLesson}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Title (English)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lessonForm.title}
                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                    placeholder="e.g., Introduction to Hiragana"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Title (Japanese)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lessonForm.titleJapanese}
                    onChange={(e) => setLessonForm({ ...lessonForm, titleJapanese: e.target.value })}
                    placeholder="例：ひらがな入門"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  rows="2"
                  value={lessonForm.description}
                  onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                  placeholder="Brief description of the lesson..."
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Lesson Content</label>
                <textarea
                  className="form-input"
                  rows="4"
                  value={lessonForm.content}
                  onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                  placeholder="Detailed lesson content, notes, and materials..."
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Lesson Order</label>
                  <input
                    type="number"
                    className="form-input"
                    min="1"
                    value={lessonForm.lessonOrder}
                    onChange={(e) => setLessonForm({ ...lessonForm, lessonOrder: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Duration (minutes)</label>
                  <select
                    className="form-select"
                    value={lessonForm.durationMinutes}
                    onChange={(e) => setLessonForm({ ...lessonForm, durationMinutes: parseInt(e.target.value) })}
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                  </select>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowLessonForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Lesson
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lessons;
